import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';

export function Encrpty(message: string, key: string) {
  return CryptoJS.AES.encrypt(message, key);
}

export function Decrypt(message: string, key: string) {
  try {
    return CryptoJS.AES.decrypt(message, key).toString(CryptoJS.enc.Utf8) || 'null';
  } catch {
    return 'null';
  }
}

export function EncryptPayload(message: string) {
  const secretKey = import.meta.env.VITE_PAYLOAD_ENCRYPT_KEY; // 32-byte key for AES-256

  // Generate a random IV for AES encryption
  const iv = CryptoJS.lib.WordArray.random(16);

  // Encrypt using AES-CBC with padding
  const cipherText = CryptoJS.AES.encrypt(
    message,
    CryptoJS.enc.Utf8.parse(secretKey),
    {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }
  );

  // Generate HMAC over ciphertext
  const hmac = CryptoJS.HmacSHA256(
    cipherText.ciphertext,
    CryptoJS.enc.Utf8.parse(secretKey)
  );

  // Combine IV + HMAC + Ciphertext in base64 format
  const encryptedData = iv.concat(hmac).concat(cipherText.ciphertext);
  return encryptedData.toString(CryptoJS.enc.Base64);
}

export const encrptionKey = 'key';
const userKey = 'user';
const wpKey = 'welcome_page';
const biodataKey = 'biodata_list';
export const statusKey = 'status';
const testListKey = 'test_list';
const testKey = 'test_question';
const answerKey = 'answer';
const roleplayKey = 'roleplay';
const timerKey = 'timer';
const catData = 'cat_data';
const logoutTimeKey = 'logout_time_key';
const resumeKey = 'resume_key';

function randomString(length: number) {
  let result = '';
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}

export type UserData = {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  gender: 'Male' | 'Female';
  language: 'indonesia' | 'english';
};

export type BiodataDetail = {
  label_alias: string;
  is_required: string;
  field_type: string;
  category: string;
  field_name: string;
};

export type BiodataData = {
  biodata_template_id: number;
  biodata_form: Array<BiodataDetail>;
};

export type WPData = {
  wpage_titla: string;
  wpage_content: string;
};

export type LoginData = UserData &
  BiodataData & {
    token: string;
    welcome_page: WPData;
    updated_biodata: boolean;
    bw_check: boolean;
    random_capture: boolean;
    proctoring: boolean;
    min_bandwidth: number;

    // test group data
    use_test_group: boolean;
    test_group: {
      start_time: string;
      end_time: string;
      test_list: {
        label: string;
        value: number;
      }[];
    };
  };

export type StatusData = {
  updated_biodata: boolean;
  bw_check: boolean;
  min_bandwidth: number;
  random_capture: boolean;
  proctoring: boolean;
  test_index: string;
  curr_path: string;

  // test group data
  use_test_group: boolean;
  test_group: {
    start_time: string;
    end_time: string;
    test_list: {
      label: string;
      value: number;
    }[];
  };
};

export type ResumeItem = {
  name: string;
  slug: string;
  year: number;
};

export type ResumeData = {
  classData: ResumeItem[];
  fieldData: ResumeItem[];
  sectorData: ResumeItem[];
  skillData: ResumeItem[];
  reasonData: string;
};

export function SaveLoginData(data: LoginData) {
  // denerate random key
  const key_enc = randomString(16);
  localStorage.setItem(encrptionKey, key_enc);

  // set timer
  localStorage.setItem(timerKey, '0');

  // sert user data
  const user_data: UserData = {
    username: data.username,
    first_name: data.first_name,
    last_name: data.last_name,
    email: data.email,
    gender: data.gender,
    language: data.language,
  };
  localStorage.setItem(
    userKey,
    Encrpty(JSON.stringify(user_data), key_enc).toString()
  );

  // set logout time
  localStorage.setItem(
    logoutTimeKey,
    Encrpty(
      String(Math.floor(Date.now() / 1000) + 24 * 3600),
      key_enc
    ).toString()
  );

  // set welcome page
  localStorage.setItem(
    wpKey,
    Encrpty(JSON.stringify(data.welcome_page), key_enc).toString()
  );

  // set biodata list
  const biodata_list: BiodataData = {
    biodata_template_id: data.biodata_template_id,
    biodata_form: data.biodata_form,
  };
  localStorage.setItem(
    biodataKey,
    Encrpty(JSON.stringify(biodata_list), key_enc).toString()
  );

  // set status data
  const status_data: StatusData = {
    updated_biodata: data.updated_biodata,
    bw_check: data.bw_check,
    min_bandwidth: data.min_bandwidth,
    random_capture: data.random_capture,
    proctoring: data.proctoring,
    test_index: '',
    curr_path: '',
    use_test_group: data.use_test_group,
    test_group: data.test_group,
  };
  localStorage.setItem(
    statusKey,
    Encrpty(JSON.stringify(status_data), key_enc).toString()
  );
}

export function SaveStatus(data?: StatusData) {
  const key_enc = localStorage.getItem(encrptionKey);
  if (!key_enc) return;

  localStorage.setItem(
    statusKey,
    Encrpty(JSON.stringify(data ? data : {}), key_enc).toString()
  );
}

export type TestDetail = {
  id: number;
  status: number;
  test_name: string;
  total_questions: number;
  test_time: string;
  data_index: string;
  open_test: boolean;
};

export type TestListData = {
  bw_check: boolean;
  test_list: Array<TestDetail>;
};

export function SaveTestListData(data: TestListData) {
  const key_enc = localStorage.getItem(encrptionKey);
  if (!key_enc) return;

  localStorage.setItem(
    testListKey,
    Encrpty(JSON.stringify(data), key_enc).toString()
  );
}

export function SetTestListStatus(test_index: string, status: number) {
  const test_list = GetTestList();
  if (test_list) {
    const test_detail = test_list.test_list.find(
      (item) => item.data_index === test_index
    );
    if (test_detail) {
      test_detail.status = status;
      SaveTestListData(test_list);
    }
  }
}

export function SetOpenTestStatus(test_index: string) {
  const test_list = GetTestList();
  if (test_list) {
    const test_detail = test_list.test_list.find(
      (item) => item.data_index === test_index
    );
    if (test_detail) {
      test_detail.open_test = true;
      SaveTestListData(test_list);
    }
  }
}

export function GetOpenTestStatus(test_index: string): boolean {
  const test_list = GetTestList();
  if (test_list) {
    const test_detail = test_list.test_list.find(
      (item) => item.data_index === test_index
    );
    if (test_detail) {
      return test_detail.open_test;
    }
  }
  return false;
}

export type FieldType =
  | { 'text-field': string }
  | { 'text-area': string }
  | { select: string | string[] | Record<string, string> };

export type Questions = {
  cluster: string;
  mandatory: boolean;
  multiple_row: boolean;
  question_per_column: string | Record<string, FieldType>;
  question: string;
  word_limit: number;
  title: string;
  comment: string;
};

interface VideoData {
  path_lowres: string;
  path_medres: string;
  path_original: string;
  response_competencies: {
    K: number;
    L: number;
    M: number;
  };
  responses: {
    K: string;
    L: string;
    M: string;
  };
  video_code: string;
  video_id: number;
  video_name: string;
  video_narration: string | null;
}

export type VideoMap<Keys extends string> = Record<Keys, VideoData>;

export type QuestionData = {
  no: number;
  situation: string;
  sample_explainer: string;
  question: string;
  questions: {
    [key: string]: string | Questions;
  };
  options: {
    [key: string]: string;
  };
  image_options: {
    [key: string]: string;
  };
  id_item: number;
  answer_type: string;
  q1: string;
  q2: string;
  value: string;
  duration: number;
  retake: number;
  video_group_code: string;
  videos: VideoMap<'X'> | VideoMap<'K' | 'L' | 'M'>;
  case: string;
  q_type: string;
};

export type TestData = {
  id: number;
  test_name: string;
  test_code: string;
  type: string;
  category: string;
  total_test: number;
  total_sample: number;
  sample_questions: {
    [key: string]: QuestionData;
  };
  test_questions: {
    [key: string]: QuestionData;
  };
  time_est: string;
  test_time: boolean;
  test_navigation: boolean;
  answer_sequence: boolean;
  answer_all: boolean;
  instruction_description: string;
  instruction_note: string;
  scenario: string;
  opening_path: string;
  material_path: string;
};

export function SaveTestData(data: TestData, index: string) {
  const key_enc = localStorage.getItem(encrptionKey);
  if (!key_enc) return;

  const new_data = {
    test_index: index,
    test_data: data,
  };
  localStorage.setItem(
    testKey,
    Encrpty(JSON.stringify(new_data), key_enc).toString()
  );
}

export type CatTempTest = {
  question: string;
  option_list: string[];
  background: string | null;
};

export function SaveCATData(data: CatTempTest) {
  const key_enc = localStorage.getItem(encrptionKey);
  if (!key_enc) return;

  localStorage.setItem(
    catData,
    Encrpty(JSON.stringify(data), key_enc).toString()
  );
}

export function GetCATData(): CatTempTest | null {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(catData);
  if (!encrypted_data || !key_enc) return null;

  const decrypted_data = Decrypt(encrypted_data, key_enc);
  return JSON.parse(decrypted_data);
}

export type AnswerData = {
  test_index: string;
  time_passed: number;
  last_question: number;
  list_number_of_answer: number[];
  answers: {
    question_id: string;
    answer: number | number[] | string | { [key: string]: any };
    // interview
    retake?: number;
    duration?: number;
    // cat
    time_spent?: number;
    // roleplay
    retry?: number;
    last_second?: number;
    is_video_playing?: boolean;
  }[];
};

export type Roleplay = {
  retry: number;
  lastSecond: number;
  isVideoPlaying: boolean;
};

export function SaveRoleplay(data: Roleplay) {
  const key_enc = localStorage.getItem(encrptionKey);
  if (!key_enc) return;

  localStorage.setItem(
    roleplayKey,
    Encrpty(JSON.stringify(data), key_enc).toString()
  );
}

export function SaveAnswerData(data: AnswerData) {
  const key_enc = localStorage.getItem(encrptionKey);
  if (!key_enc) return;

  localStorage.setItem(
    answerKey,
    Encrpty(JSON.stringify(data), key_enc).toString()
  );
}

export function SaveTimer(data: number) {
  localStorage.setItem(timerKey, String(data));
}

export function SetPath(path: string) {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(statusKey);
  if (!encrypted_data || !key_enc) return;

  const decrypted_data = Decrypt(encrypted_data, key_enc);
  const local_data = JSON.parse(decrypted_data);
  local_data.curr_path = path;

  SaveStatus(local_data);
}

export function SetTestIndex(index: string) {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(statusKey);
  if (!encrypted_data || !key_enc) return;

  const decrypted_data = Decrypt(encrypted_data, key_enc);
  const local_data = JSON.parse(decrypted_data);
  local_data.test_index = index;

  SaveStatus(local_data);
}

export function AlreadyBW() {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(statusKey);
  if (!encrypted_data || !key_enc) return;

  const decrypted_data = Decrypt(encrypted_data, key_enc);
  const local_data = JSON.parse(decrypted_data);
  local_data.bw_check = true;

  SaveStatus(local_data);
}

export function AlreadyUpdateBiodata() {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(statusKey);
  if (!encrypted_data || !key_enc) return;

  const decrypted_data = Decrypt(encrypted_data, key_enc);
  const local_data = JSON.parse(decrypted_data);
  local_data.updated_biodata = true;

  SaveStatus(local_data);
}

export function SetAnswer(index: string, answer: number | number[] | string) {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(answerKey);
  if (!encrypted_data || !key_enc) return;

  const decrypted_data = Decrypt(encrypted_data, key_enc);
  const local_data = JSON.parse(decrypted_data);
  local_data.answers[index].answer = answer;

  SaveAnswerData(local_data);
}

export function SetQuestionIndex(index: number) {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(answerKey);
  if (!encrypted_data || !key_enc) return;

  const decrypted_data = Decrypt(encrypted_data, key_enc);
  const local_data = JSON.parse(decrypted_data);
  local_data.last_question = index;

  SaveAnswerData(local_data);
}

const resumeDataTranslations = {
  id: {
    classData: [
      { name: 'Kelas 1', slug: 'kelas-1' },
      { name: 'Kelas 2', slug: 'kelas-2' },
      { name: 'Kelas 3', slug: 'kelas-3' },
      { name: 'Kelas 4', slug: 'kelas-4' },
      { name: 'Kelas 5', slug: 'kelas-5' },
    ],
    fieldData: [
      { name: 'Komersial', slug: 'commercial' },
      { name: 'Keuangan', slug: 'finance' },
      { name: 'Operasi', slug: 'operation' },
      { name: 'Teknologi', slug: 'technology' },
      { name: 'SDM', slug: 'hr' },
    ],
    sectorData: [
      { name: 'Energi & Migas', slug: 'energi-migas' },
      { name: 'Mineral & Batubara', slug: 'mineral-batubara' },
      { name: 'Perkebunan & Kehutanan', slug: 'perkebunan-kehutanan' },
      { name: 'Pangan & Pupuk', slug: 'pangan-pupuk' },
      { name: 'Kesehatan', slug: 'kesehatan' },
      { name: 'Manufaktur', slug: 'manufaktur' },
      { name: 'Keuangan', slug: 'keuangan' },
      { name: 'Asuransi & Dana Pensiun', slug: 'asuransi-pensiun' },
      { name: 'Telekomunikasi & Media', slug: 'telekomunikasi' },
      { name: 'Infrastruktur', slug: 'infrastruktur' },
      { name: 'Logistik', slug: 'logistik' },
      { name: 'Pariwisata & Pendukung', slug: 'pariwisata-pendukung' },
      { name: 'Danareksa', slug: 'danareksa' },
    ],
    skillData: [
      { name: 'Keuangan', slug: 'keuangan' },
      { name: 'Pengembangan Usaha/R&D', slug: 'pengembangan-usaha' },
      { name: 'Treasury', slug: 'treasury' },
      { name: 'Direktur Utama', slug: 'direktur-utama' },
      { name: 'Sistem Management & Safety', slug: 'sistem-management-safety' },
      { name: 'Engineering & Maintenance', slug: 'engineering-maintenance' },
      { name: 'SDM/Human Capital', slug: 'sdm' },
      { name: 'Digitalisasi & IT', slug: 'digitalisasi-it' },
      {
        name: 'Logistik/Supply Chain/Asset/GA',
        slug: 'logistik-supply-chain-asset-ga',
      },
      { name: 'Bisnis/Niaga/Pemasaran', slug: 'bisnis-niaga-pemasaran' },
      { name: 'Legal & Compliance', slug: 'legal-compliance' },
      { name: 'Operasi/Produk/Proyek', slug: 'operasi-produk-proyek' },
      { name: 'Risiko Bisnis/Enterprise Risk', slug: 'risiko' },
    ],
  },
  en: {
    classData: [
      { name: 'Class 1', slug: 'kelas-1' },
      { name: 'Class 2', slug: 'kelas-2' },
      { name: 'Class 3', slug: 'kelas-3' },
      { name: 'Class 4', slug: 'kelas-4' },
      { name: 'Class 5', slug: 'kelas-5' },
    ],
    fieldData: [
      { name: 'Commercial', slug: 'commercial' },
      { name: 'Finance', slug: 'finance' },
      { name: 'Operations', slug: 'operation' },
      { name: 'Technology', slug: 'technology' },
      { name: 'Human Resources', slug: 'hr' },
    ],
    sectorData: [
      { name: 'Energy & Oil & Gas', slug: 'energi-migas' },
      { name: 'Mineral & Coal', slug: 'mineral-batubara' },
      { name: 'Plantation & Forestry', slug: 'perkebunan-kehutanan' },
      { name: 'Food & Fertilizer', slug: 'pangan-pupuk' },
      { name: 'Health', slug: 'kesehatan' },
      { name: 'Manufacturing', slug: 'manufaktur' },
      { name: 'Finance', slug: 'keuangan' },
      { name: 'Insurance & Pension Fund', slug: 'asuransi-pensiun' },
      { name: 'Telecommunication & Media', slug: 'telekomunikasi' },
      { name: 'Infrastructure', slug: 'infrastruktur' },
      { name: 'Logistics', slug: 'logistik' },
      { name: 'Tourism & Supporting Services', slug: 'pariwisata-pendukung' },
      { name: 'Investment/Securities', slug: 'danareksa' },
    ],
    skillData: [
      { name: 'Finance', slug: 'keuangan' },
      { name: 'Business Development/R&D', slug: 'pengembangan-usaha' },
      { name: 'Treasury', slug: 'treasury' },
      { name: 'CEO/President Director', slug: 'direktur-utama' },
      { name: 'Management System & Safety', slug: 'sistem-management-safety' },
      { name: 'Engineering & Maintenance', slug: 'engineering-maintenance' },
      { name: 'HR/Human Capital', slug: 'sdm' },
      { name: 'Digitalization & IT', slug: 'digitalisasi-it' },
      {
        name: 'Logistics/Supply Chain/Asset/GA',
        slug: 'logistik-supply-chain-asset-ga',
      },
      { name: 'Business/Commerce/Marketing', slug: 'bisnis-niaga-pemasaran' },
      { name: 'Legal & Compliance', slug: 'legal-compliance' },
      { name: 'Operations/Product/Project', slug: 'operasi-produk-proyek' },
      { name: 'Business Risk/Enterprise Risk', slug: 'risiko' },
    ],
  },
};

const addYearProperty = (
  data: { name: string; slug: string }[]
): ResumeItem[] => {
  return data.map((item) => ({ ...item, year: 0 }));
};

export function SetDefaultResumeData(
  isEnglish: boolean = false
): ResumeData | null {
  const key_enc = localStorage.getItem(encrptionKey);
  if (!key_enc) return null;

  const translationSet = isEnglish
    ? resumeDataTranslations.en
    : resumeDataTranslations.id;

  const defaultResume: ResumeData = {
    classData: addYearProperty(translationSet.classData),
    fieldData: addYearProperty(translationSet.fieldData),
    sectorData: addYearProperty(translationSet.sectorData),
    skillData: addYearProperty(translationSet.skillData),
    reasonData: '',
  };

  localStorage.setItem(
    resumeKey,
    Encrpty(JSON.stringify(defaultResume), key_enc).toString()
  );

  return defaultResume;
}

export function SaveResumeData(data: ResumeData) {
  const key_enc = localStorage.getItem(encrptionKey);
  if (!key_enc) return;

  localStorage.setItem(
    resumeKey,
    Encrpty(JSON.stringify(data), key_enc).toString()
  );
}

export function GetUser(): UserData | null {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(userKey);
  if (!encrypted_data || !key_enc) return null;

  const decrypted_data = Decrypt(encrypted_data, key_enc);
  return JSON.parse(decrypted_data);
}

export function GetLogoutTime(): number {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(logoutTimeKey);
  if (!encrypted_data || !key_enc) return 0;

  const decrypted_data = Decrypt(encrypted_data, key_enc);
  return Number(decrypted_data);
}

export function GetWelcomePage(): WPData | null {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(wpKey);
  if (!encrypted_data || !key_enc) return null;

  const decrypted_data = Decrypt(encrypted_data, key_enc);
  return JSON.parse(decrypted_data);
}

export function GetBiodataList(): BiodataData | null {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(biodataKey);
  if (!encrypted_data || !key_enc) return null;

  const decrypted_data = Decrypt(encrypted_data, key_enc);
  return JSON.parse(decrypted_data);
}

export function GetStatus(): StatusData | null {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(statusKey);
  if (!encrypted_data || !key_enc) return null;

  const decrypted_data = Decrypt(encrypted_data, key_enc);
  return JSON.parse(decrypted_data);
}

export function GetTestList(): TestListData | null {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(testListKey);
  if (!encrypted_data || !key_enc) return null;

  const decrypted_data = Decrypt(encrypted_data, key_enc);
  return JSON.parse(decrypted_data);
}

export function GetTest(): { test_index: string; test_data: TestData } | null {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(testKey);
  if (!encrypted_data || !key_enc) return null;
  const decrypted_data = Decrypt(encrypted_data, key_enc);
  return JSON.parse(decrypted_data);
}

export function GetAnswer(): AnswerData | null {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(answerKey);
  if (!encrypted_data || !key_enc) return null;

  const decrypted_data = Decrypt(encrypted_data, key_enc);
  return JSON.parse(decrypted_data);
}

export function GetRoleplay(): Roleplay | null {
  const key_enc = localStorage.getItem(encrptionKey);
  const encrypted_data = localStorage.getItem(roleplayKey);
  if (!encrypted_data || !key_enc) return null;

  const decrypted_data = Decrypt(encrypted_data, key_enc);
  return JSON.parse(decrypted_data);
}

export function GetTimer(): number {
  const time = localStorage.getItem(timerKey);
  return time ? Number(time) : 0;
}

export function GetAllStore() {
  const result = [];
  result.push({
    key: userKey,
    value: GetUser() && JSON.stringify(GetUser(), null, 2),
  });
  // result.push({
  //   key: wpKey,
  //   value: GetWelcomePage() && JSON.stringify(GetWelcomePage(), null, 2),
  // });
  // result.push({
  //   key: biodataKey,
  //   value: GetBiodataList() && JSON.stringify(GetBiodataList(), null, 2),
  // });
  result.push({
    key: statusKey,
    value: GetStatus() && JSON.stringify(GetStatus(), null, 2),
  });
  result.push({
    key: testListKey,
    value: GetTestList() && JSON.stringify(GetTestList(), null, 2),
  });
  result.push({
    key: testKey,
    value: GetTest() && JSON.stringify(GetTest(), null, 2),
  });
  result.push({
    key: answerKey,
    value: GetAnswer() && JSON.stringify(GetAnswer(), null, 2),
  });
  // result.push({
  //   key: timerKey,
  //   value: localStorage.getItem(timerKey),
  // });
  result.push({
    key: catData,
    value: GetCATData() && JSON.stringify(GetCATData(), null, 2),
  });

  return result;
}

export function GetAllResume() {
  let resume: ResumeData = {
    classData: [],
    fieldData: [],
    sectorData: [],
    skillData: [],
    reasonData: '',
  };

  const key_enc = localStorage.getItem(encrptionKey);

  const resume_encrypted_data = localStorage.getItem(resumeKey);

  if (resume_encrypted_data && key_enc) {
    resume = JSON.parse(Decrypt(resume_encrypted_data, key_enc));
  }

  return resume;
}

export function DeleteAllStore() {
  localStorage.removeItem(encrptionKey);
  localStorage.removeItem(userKey);
  localStorage.removeItem(wpKey);
  localStorage.removeItem(biodataKey);
  localStorage.removeItem(statusKey);
  localStorage.removeItem(testListKey);
  localStorage.removeItem(testKey);
  localStorage.removeItem(answerKey);
  localStorage.removeItem(timerKey);
  localStorage.removeItem(catData);
  localStorage.removeItem(logoutTimeKey);
  localStorage.removeItem(resumeKey);
  localStorage.removeItem(roleplayKey);

  Cookies.remove('access_token');
}

export function DeleteTestAndAnswerData() {
  localStorage.removeItem(testKey);
  localStorage.removeItem(answerKey);
  localStorage.removeItem(roleplayKey);
}

export function ResetAnswerData() {
  const answer = GetAnswer();
  if (answer) {
    answer.time_passed = 0;
    answer.last_question = 0;
    answer.list_number_of_answer = [];
    if (Array.isArray(answer.answers)) {
      answer.answers = answer.answers.map((item) => ({ ...item, answer: 0 }));
    }
    SaveAnswerData(answer);
  }
}

export function DeleteCATData() {
  localStorage.removeItem(catData);
}
